import { fbt } from 'fbtee'
import invariant from 'tiny-invariant'
import { Button } from '../../../components/ui/button'
import { UserRow } from '../../../components/UserRow'
import { unknownMemberName } from '../../../lib/commonStrings'
import { placeholders } from '../../../lib/skeletonUtils'
import type { UserProfile } from '../../../types'

/** People in the viewer's crew looking for a ticket; a buyer with an open one can hand it over. */
export function TicketSeekersList({ loading, seekerIds, profiles, profilesLoading, busy, onGive }: {
  loading?: boolean
  seekerIds?: string[]
  profiles?: Record<string, UserProfile>
  profilesLoading?: boolean
  busy?: boolean
  onGive?: (uid: string) => void
}) {
  const heading = (
    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
      {fbt('Looking for a ticket', 'Heading above the people in the viewer’s crew who are looking for a ticket')}
    </p>
  )
  if (loading) {
    return <div className="space-y-2">{heading}<ul>{placeholders(1).map((_, i) => <UserRow key={i} loading />)}</ul></div>
  }
  invariant(seekerIds, 'TicketSeekersList: seekerIds is required when not loading')
  if (seekerIds.length === 0) return null

  return (
    <div className="space-y-2" data-testid="ticket-seekers">
      {heading}
      <ul className="flex flex-col gap-2">
        {seekerIds.map(uid => {
          const profile = profiles?.[uid]
          if (!profile && profilesLoading) return <UserRow key={uid} loading />
          return (
            <UserRow
              key={uid}
              name={profile?.displayName?.trim() || unknownMemberName()}
              profile={profile}
              action={onGive && (
                <Button size="sm" variant="outline" disabled={busy} onClick={() => onGive(uid)}>
                  {fbt('Give my ticket', 'Button assigning one of the viewer’s open tickets to someone looking for one')}
                </Button>
              )}
            />
          )
        })}
      </ul>
    </div>
  )
}
